import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Building2, Users, IdCard, Clock, Loader2 } from "lucide-react";

/**
 * Phase 22 — Platform stats tab.
 *
 * Cross-tenant rollups from ``GET /api/platform/stats``: tenants,
 * identities, memberships, 30-day time entries, plus a top-10 tenants
 * table ranked by activity.
 */
export default function PlatformStatsPage() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/platform/stats");
        setStats(data);
      } catch (e) { toast.error(formatApiError(e)); }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) {
    return (
      <div className="grid place-items-center py-16 text-muted-foreground" data-testid="platform-stats-loading">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }
  if (!stats) return null;

  const kpis = [
    { label: "Workspaces", value: stats.tenants_total, icon: Building2, testid: "kpi-tenants" },
    { label: "Identities", value: stats.identities_total, icon: IdCard, testid: "kpi-identities" },
    { label: "Team members", value: stats.memberships_total, icon: Users, testid: "kpi-memberships" },
    { label: "Entries (30d)", value: stats.time_entries_last_30d, icon: Clock, testid: "kpi-entries" },
  ];

  return (
    <div className="space-y-6" data-testid="platform-stats-page">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {kpis.map((k) => (
          <Card key={k.label} data-testid={k.testid}>
            <CardContent className="flex items-center gap-3 p-4">
              <div className="grid h-10 w-10 place-items-center rounded-xl bg-secondary/10 text-secondary">
                <k.icon className="h-5 w-5" />
              </div>
              <div className="min-w-0">
                <div className="text-xs uppercase tracking-widest text-muted-foreground">{k.label}</div>
                <div className="font-display text-2xl font-semibold text-primary">
                  {(k.value ?? 0).toLocaleString()}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardContent className="p-0">
          <div className="border-b border-border px-4 py-3">
            <div className="text-sm font-semibold text-primary">Top tenants by activity</div>
            <div className="text-xs text-muted-foreground">
              Ranked by time entries in the last 30 days.
            </div>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>#</TableHead>
                <TableHead>Workspace</TableHead>
                <TableHead>Slug</TableHead>
                <TableHead className="text-right">Entries (30d)</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(stats.top_tenants_by_activity || []).map((row, i) => (
                <TableRow key={row.tenant_id} data-testid={`platform-top-row-${row.tenant_slug}`}>
                  <TableCell className="text-muted-foreground">{i + 1}</TableCell>
                  <TableCell className="font-medium">{row.tenant_name || "—"}</TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground">{row.tenant_slug}</TableCell>
                  <TableCell className="text-right font-mono">{(row.entries_30d ?? 0).toLocaleString()}</TableCell>
                </TableRow>
              ))}
              {(stats.top_tenants_by_activity || []).length === 0 && (
                <TableRow><TableCell colSpan={4} className="py-8 text-center text-muted-foreground">
                  No activity in the last 30 days.
                </TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      <div className="text-right text-[10px] text-muted-foreground" data-testid="platform-stats-generated-at">
        Generated {stats.generated_at ? new Date(stats.generated_at).toLocaleString() : "—"}
      </div>
    </div>
  );
}
